// src/utils/educatorCode.js
//
// Educator codes, allocated in the browser.
//
// WHY NOT THE CLOUD FUNCTION
// --------------------------
// `ensureEducatorCode` is unreachable — the preflight is rejected before the
// function runs, so every call comes back as `internal`. That's an
// infrastructure problem, but it also revealed that the function wasn't
// necessary in the first place.
//
// The original reasoning was: uniqueness is a privacy boundary (sameEduCode()
// in firestore.rules turns a shared code into read access), and a client
// can't guarantee uniqueness because `list` on /users is admin-only, so it
// can't check whether a code is taken.
//
// True — but checking is the wrong approach. A transaction on a single
// counter document never issues the same number twice, and needs no query at
// all. Firestore transactions are atomic and retry on contention, so two
// simultaneous signups cannot both take the same value. That's exactly how
// AdminRoles and createUserWithStudents already allocate codes today.
//
// So: one counter, one transaction, no server round-trip, no CORS.
//
// THE DIRECTORY
// -------------
// A parent registering needs to turn a code into "is this the right teacher?"
// That's a lookup by code, and `list` on /users is admin-only.
//
// `educatorDirectory/{code}` solves it with a document GET instead of a
// query. It holds only what the confirmation step needs — the educator's
// display name — so looking up a code reveals a name and nothing else. No
// email, no student list, no profile access.

import {
  doc,
  collection,
  getDoc,
  getDocs,
  setDoc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase/firebaseConfig";

// Matches the existing series in AdminRoles/createUserWithStudents, so codes
// issued before and after this change belong to the same sequence and can't
// collide with each other.
const COUNTER_START = 471020;
const COUNTER_STEP = 5;

// ---------------------------------------------------------------------------
// SERIES — how to start a new run of codes a leading digit higher
//
// Bumping SERIES by one makes the NEXT code issued jump to the same number
// with its first digit increased by one, then carry on in steps from there:
//
//     471020  ->  571020  ->  571025  ->  571030 …
//      42000  ->   52000
//     420000  ->  520000
//
// Only the leading digit moves; the rest of the number is kept, which is what
// makes the jump obvious at a glance when you're looking at a list of codes.
//
// WHY A SERIES NUMBER RATHER THAN JUST RAISING COUNTER_START
// ----------------------------------------------------------
// Raising the constant would do nothing. The counter document already exists
// and holds a `next` value, so the constant is only ever read on the very
// first allocation — every call after that reads the stored number and
// ignores COUNTER_START entirely.
//
// The series marker is stored alongside the counter. When the code sees a
// series it hasn't applied yet, it rebases once, records the new series, and
// then behaves normally. That makes it idempotent: two educators signing up
// at the same moment can't both apply the bump, and reloading the page a
// hundred times won't keep pushing the numbers up.
//
// It also means you don't need to know what number the counter is currently
// on. The bump is computed from whatever it actually holds.
//
// To start another run higher: change this to 3, deploy, done.
const SERIES = 2;

/**
 * Raises the most significant digit by one, keeping every other digit.
 *
 * 471020 -> 571020, 42000 -> 52000, 999 -> 1099 (the number simply grows a
 * digit when the leading one would pass 9, which is the sane thing to do and
 * keeps the sequence increasing).
 */
export function bumpLeadingDigit(n) {
  const value = Math.max(0, Math.floor(Number(n) || 0));
  if (value < 10) return value + 10;
  const magnitude = 10 ** (String(value).length - 1);
  return value + magnitude;
}

// Roles that get a code of their own. A student or parent TYPES an educator's
// code at signup — issuing them one as well would burn numbers and, worse,
// give them a code other people could link themselves to.
const CODE_BEARING_ROLES = ["educator", "district"];

function shouldHaveOwnCode(profile = {}) {
  // A seat holder inherits the buyer's code (see claimSeat). Giving them
  // their own would detach them from the classroom they belong to.
  if (profile.seatSourceOf) return false;

  const granted = String(profile.role || "").toLowerCase();
  if (granted.includes("educator")) return true;
  if (granted.includes("student")) return false;

  // Role is still "unpaid" until the Stripe webhook promotes it, so the
  // signup intent is what answers this for most accounts.
  const intended = String(profile.intendedRole || "").toLowerCase();
  return CODE_BEARING_ROLES.includes(intended);
}

/**
 * The educator's code, allocated once and never changed.
 *
 * Safe to call on every dashboard load: if the profile already has a code it
 * returns immediately without touching the counter. That's what makes a
 * "Generate code" button unnecessary — there is nothing for the educator to
 * decide or trigger.
 *
 * @param {string} uid
 * @param {object} profile the user's existing profile data
 * @returns {Promise<string>} the code
 */
export async function ensureEducatorCode(uid, profile = {}) {
  if (!uid) throw new Error("No user.");

  // Already has one — this is the common path on every page load.
  if (profile.educatorCode) return String(profile.educatorCode);

  // Students, parents, and seat holders don't get one of their own.
  if (!shouldHaveOwnCode(profile)) return profile.educatorCode || "";

  const counterRef = doc(db, "counters", "educatorCode");

  const code = await runTransaction(db, async (tx) => {
    // Re-read the profile inside the transaction. Two tabs open on the same
    // account would otherwise both pass the check above and burn two numbers.
    const userSnap = await tx.get(doc(db, "users", uid));
    const existing = userSnap.data()?.educatorCode;
    if (existing) return String(existing);

    const snap = await tx.get(counterRef);

    if (!snap.exists()) {
      // First code ever issued. Start at the series-appropriate base so a
      // fresh counter doesn't hand out a number below one already in use.
      let start = COUNTER_START;
      for (let s = 1; s < SERIES; s += 1) start = bumpLeadingDigit(start);

      tx.set(counterRef, {
        next: start + COUNTER_STEP,
        series: SERIES,
        updatedAt: serverTimestamp(),
      });
      return String(start);
    }

    const data = snap.data() || {};
    const stored =
      typeof data.next === "number" ? data.next : COUNTER_START + COUNTER_STEP;

    // A series we haven't applied yet: jump the leading digit once, take the
    // first number of the new run, and record that we've done it.
    if ((data.series || 1) < SERIES) {
      const rebased = bumpLeadingDigit(stored);
      tx.update(counterRef, {
        next: rebased + COUNTER_STEP,
        series: SERIES,
        previousNext: stored,
        rebasedAt: serverTimestamp(),
      });
      return String(rebased);
    }

    tx.update(counterRef, {
      next: stored + COUNTER_STEP,
      updatedAt: serverTimestamp(),
    });
    return String(stored);
  });

  // Written after the transaction rather than inside it, because a failure
  // here must not roll back the allocation — two educators sharing a number
  // is a privacy problem, a missing profile field is a cosmetic one that
  // heals on the next load.
  await setDoc(
    doc(db, "users", uid),
    { educatorCode: code },
    { merge: true }
  );

  await publishToDirectory(uid, code, profile);

  return code;
}

/**
 * Publishes the educator to the lookup directory so parents can confirm them
 * by code at registration.
 *
 * Non-fatal by design: a code that works but isn't yet in the directory still
 * links students correctly, it just can't be confirmed by name yet.
 */
export async function publishToDirectory(uid, code, profile = {}) {
  if (!uid || !code) return;

  const displayName =
    [profile.sirName, profile.firstName, profile.lastName]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    profile.name ||
    "Educator";

  try {
    await setDoc(
      doc(db, "educatorDirectory", String(code)),
      {
        uid,
        displayName,
        // The parts, not just the joined string.
        //
        // A student's subject page greets them with "Welcome to Mr. Eric's
        // Math class" — that needs the title and the LAST name specifically,
        // and you can't reliably pull those back out of "Mr. Martin Eric"
        // (two-word surnames, missing titles, names stored in a different
        // order). Storing them separately costs three fields and removes a
        // guess. See utils/displayName.js.
        sirName: profile.sirName || "",
        firstName: profile.firstName || "",
        lastName: profile.lastName || "",
        state: profile.state || "",
        grade: profile.grade || "",
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (err) {
    console.warn("Educator directory not updated:", err?.message || err);
  }
}

/**
 * Turns a code into an educator's name, for the confirm-before-you-link step.
 *
 * @returns {Promise<{code, uid, displayName, state, grade}|null>} null when
 *          no educator uses that code.
 */
export async function lookupEducatorByCode(code) {
  const clean = String(code || "").trim().toUpperCase();
  if (!clean) return null;

  // Fast path: read the directory directly (works when signed in).
  try {
    const snap = await getDoc(doc(db, "educatorDirectory", clean));
    if (snap.exists() && snap.data()?.uid) return { code: clean, ...snap.data() };
  } catch {
    // Signed out (the sign-up page) — the directory needs a sign-in. Fall
    // through to the server, which can always look.
  }

  // The server checks signed-out too, and repairs a missing entry (educators
  // created by an admin never had one) — functions/educatorLookup.js.
  const res = await httpsCallable(functions, "findEducatorByCode")({ code: clean });
  return res?.data?.found ? res.data.educator : null;
}

/**
 * Keeps an educator's directory entry current.
 *
 * The entry is written once when the code is allocated, so a teacher who
 * later adds a title or corrects a surname would keep greeting their class
 * under the old name. Cheap to call on dashboard load; skips the write when
 * nothing has changed.
 */
export async function refreshDirectoryEntry(uid, profile = {}) {
  const code = profile.educatorCode;
  if (!uid || !code) return;

  try {
    const snap = await getDoc(doc(db, "educatorDirectory", String(code)));
    const current = snap.exists() ? snap.data() : null;

    const same =
      current &&
      current.sirName === (profile.sirName || "") &&
      current.firstName === (profile.firstName || "") &&
      current.lastName === (profile.lastName || "");

    if (same) return;

    await publishToDirectory(uid, code, profile);
  } catch (err) {
    console.warn("Directory refresh skipped:", err?.message || err);
  }
}

/**
 * Adds a student or parent to an educator's roster.
 *
 * WHY A ROSTER AND NOT A QUERY
 * ----------------------------
 * "View Students" ran:
 *
 *   query(collection(db, "users"), where("educatorCode", "==", code), ...)
 *
 * That's a `list` on /users, and firestore.rules allows `list` only to
 * admins — so it failed for every educator, trial or paid. And Firestore
 * rules can't inspect a query's where-clause values, so there's no way to
 * write a rule that says "you may list users, but only ones sharing your
 * code". The permission simply can't be expressed at that shape.
 *
 * A subcollection can: `educatorRosters/{code}/students/{uid}` is listable by
 * whoever owns that code, and by nobody else. The student writes their own
 * entry when they link, so no elevated permission is needed on either side.
 *
 * It also fixes a second bug in the old query: it filtered on
 * `role in ["student", "va-3rd-student"]`, but everyone's role is "unpaid"
 * until Stripe promotes them — so even with list access it would have
 * returned nothing during a trial.
 */
export async function joinEducatorRoster(uid, code, profile = {}) {
  if (!uid || !code) return;

  try {
    await setDoc(
      doc(db, "educatorRosters", String(code), "students", uid),
      {
        uid,
        firstName: profile.firstName || "",
        lastName: profile.lastName || "",
        email: profile.email || "",
        grade: profile.grade || "",
        state: profile.state || "",
        // Denormalised so the classroom can draw a roster without a
        // per-student /users read. Refreshed by refreshRosterEntry below.
        avatarUrl: profile.avatarUrl || "",
        intendedRole: profile.intendedRole || profile.role || "",
        linkedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (err) {
    // Non-fatal: the link on the profile still holds, the student just won't
    // appear in the class list until this succeeds.
    console.warn("Could not join educator roster:", err?.message || err);
  }
}

/**
 * The educator's class list. Readable only by the owner of the code.
 */
// once per page load: ask the server to add any student whose profile has
// this code but who never got a class-list row (made by staff, seat, code
// typed in later…). Never blocks the list for more than a few seconds.
const synced = new Map();
function syncRosterOnce(code) {
  const key = String(code);
  if (!synced.has(key)) {
    const call = httpsCallable(functions, "syncMyRoster")({}).catch((err) => {
      console.warn("Roster sync skipped:", err?.code || err?.message || err);
      synced.delete(key); // try again next time
    });
    synced.set(key, Promise.race([call, new Promise((r) => setTimeout(r, 6000))]));
  }
  return synced.get(key);
}

export async function listRoster(code) {
  if (!code) return [];

  await syncRosterOnce(code);

  const snap = await getDocs(
    collection(db, "educatorRosters", String(code), "students")
  );

  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Keeps a student's roster row in step with their profile.
 *
 * The row is written once at signup, so a child who later picks an avatar or
 * has their name corrected would keep showing the old values in their
 * teacher's classroom. Cheap to call when either changes.
 *
 * Non-fatal by design — a stale avatar is cosmetic, and failing here must not
 * block the profile edit that triggered it.
 */
export async function refreshRosterEntry(uid, code, profile = {}) {
  if (!uid || !code) return;

  try {
    await setDoc(
      doc(db, "educatorRosters", String(code), "students", uid),
      {
        firstName: profile.firstName || "",
        lastName: profile.lastName || "",
        avatarUrl: profile.avatarUrl || "",
        grade: profile.grade || "",
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (err) {
    console.warn("Could not refresh roster entry:", err?.message || err);
  }
}
